import { ConfigService } from './config.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, first, map, Observable } from 'rxjs';
import { KPI } from '../api';
import {
  BUDGET_OR_RATIO_FILTER_STORAGE_KEY,
  BUDGET_OR_RATIO_CARBON_FILTER_STORAGE_KEY,
  FEASIBILITY_CONSUMPTION_OR_EMISSION_FILTER_STORAGE_KEY,
  FORECAST_EMISSION_FACTOR_FILTER_STORAGE_KEY,
  INITIATIVE_PRIORITIES_BY_TYPE_FILTER_STORAGE_KEY,
  INITIATIVE_PRIORITIES_FILTER_STORAGE_KEY,
  INITIATIVE_SORTING_PRIORITY_FILTER_STORAGE_KEY,
  INVESTMENTS_STATUS,
  PLANNED_INITIATIVE_FILTER_STORAGE_KEY,
  ENERGY_MEASUREMENTS_KEY,
  CONSUMPTION_AGGREGATION_KEY,
  ENERGY_CONSUMPTION_DATA_KEY,
  ENERGY_CONSUMPTION_DISPLAY_KEY,
  ENERGY_CONSUMPTION_BASELINEDEVIATION_KEY,
  EMISSION_AGGREGATION_KEY,
  ENERGY_EMISSION_DATA_KEY,
  ENERGY_EMISSION_DISPLAY_KEY,
  ENERGY_EMISSION_BASELINEDEVIATION_KEY,
  DECISION_OVERVIEW_FILTER_STORAGE_KEY,
  ENERGY_CONSUMPTION_TYPE_STORAGE_KEY,
  CARBON_CONSUMPTION_TYPE_STORAGE_KEY,
  ENERGY_OR_CARBON_TRAJECTORY_KEY,
  THEORETICAL_OR_AUDIT_FILTER_STORAGE_KEY,
} from './constants';

export interface ObservableMap<T> {
  [key: string]: BehaviorSubject<T>;
}

export type FilterType = 'budgetOrRatio'
  | 'budgetOrRatioCarbon'
  | 'plannedInitiative'
  | 'forecastEmissionFactor'
  | 'initiativePriorities'
  | 'initiativePrioritiesByType'
  | 'initiativeSortingPriority'
  | 'feasibilityConsumptionOrEmission'
  | 'energyMeasurements'
  | 'consumptionAggregation'
  | 'energyConsumptionData'
  | 'energyConsumptionDisplay'
  | 'energyConsumptionBaselineDeviation'
  | 'emissionAggregation'
  | 'energyEmissionData'
  | 'energyEmissionDisplay'
  | 'energyEmissionBaselineDeviation'
  | 'decisionOverviewFilter'
  | 'investmentsStatus'
  | 'energyConsumptionType'
  | 'carbonConsumptionType'
  | 'energyOrCarbonTrajectory'
  | 'theoreticalOrAudit';

@Injectable({
  providedIn: 'root',
})
export class ChartFiltersService {

  private storageKeys = {
    budgetOrRatio: BUDGET_OR_RATIO_FILTER_STORAGE_KEY,
    budgetOrRatioCarbon: BUDGET_OR_RATIO_CARBON_FILTER_STORAGE_KEY,
    plannedInitiative: PLANNED_INITIATIVE_FILTER_STORAGE_KEY,
    forecastEmissionFactor: FORECAST_EMISSION_FACTOR_FILTER_STORAGE_KEY,
    initiativePriorities: INITIATIVE_PRIORITIES_FILTER_STORAGE_KEY,
    initiativePrioritiesByType: INITIATIVE_PRIORITIES_BY_TYPE_FILTER_STORAGE_KEY,
    initiativeSortingPriority: INITIATIVE_SORTING_PRIORITY_FILTER_STORAGE_KEY,
    feasibilityConsumptionOrEmission: FEASIBILITY_CONSUMPTION_OR_EMISSION_FILTER_STORAGE_KEY,
    energyMeasurements: ENERGY_MEASUREMENTS_KEY,
    consumptionAggregation: CONSUMPTION_AGGREGATION_KEY,
    energyConsumptionData: ENERGY_CONSUMPTION_DATA_KEY,
    energyConsumptionDisplay: ENERGY_CONSUMPTION_DISPLAY_KEY,
    energyConsumptionBaselineDeviation: ENERGY_CONSUMPTION_BASELINEDEVIATION_KEY,
    emissionAggregation: EMISSION_AGGREGATION_KEY,
    energyEmissionData: ENERGY_EMISSION_DATA_KEY,
    energyEmissionDisplay: ENERGY_EMISSION_DISPLAY_KEY,
    energyEmissionBaselineDeviation: ENERGY_EMISSION_BASELINEDEVIATION_KEY,
    decisionOverviewFilter: DECISION_OVERVIEW_FILTER_STORAGE_KEY,
    investmentsStatus: INVESTMENTS_STATUS,
    energyConsumptionType: ENERGY_CONSUMPTION_TYPE_STORAGE_KEY,
    carbonConsumptionType: CARBON_CONSUMPTION_TYPE_STORAGE_KEY,
    energyOrCarbonTrajectory: ENERGY_OR_CARBON_TRAJECTORY_KEY,
    theoreticalOrAudit: THEORETICAL_OR_AUDIT_FILTER_STORAGE_KEY,
  };

  private defaultFilters: { [filter: string]: number | string } = {
    budgetOrRatio: KPI.BudgetOrRatioFilter.TRAJECTORY_ONLY,
    budgetOrRatioCarbon: KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY,
    forecastEmissionFactor: KPI.ElectricityForecastEmissionFactorFilter.FIXED_FACTOR,
    initiativePriorities: KPI.InitiativePrioritiesFilter.OVERVIEW,
    initiativePrioritiesByType: null,
    initiativeSortingPriority: KPI.InitiativeSortingPriorityFilter.MOST_TO_LESS_PROFITABLE,
    investmentsStatus: KPI.InvestmentsStatusFilter.ALL_INVESTMENTS,
    plannedInitiative: KPI.PlannedInitiativeFilter.ALL,
    feasibilityConsumptionOrEmission: KPI.FeasibilityConsumptionOrEmissionFilter.CONSUMPTION,
    energyMeasurements: KPI.EnergyMeasurementsFilter.CONSO,
    consumptionAggregation: KPI.ConsumptionAggregationsFilter.CONSO,
    energyConsumptionData: KPI.EnergyConsumptionFilter.RAW,
    energyConsumptionDisplay: KPI.EnergyConsumptionFilter.MONTH,
    energyConsumptionBaselineDeviation: KPI.EnergyConsumptionFilter.PERCENT,
    emissionAggregation: KPI.EmissionAggregationsFilter.EMISSION,
    energyEmissionData: KPI.EnergyConsumptionFilter.RAW,
    energyEmissionDisplay: KPI.EnergyConsumptionFilter.TWELVESLIDING,
    energyEmissionBaselineDeviation: KPI.EnergyConsumptionFilter.PERCENT,
    decisionOverviewFilter: KPI.DecisionOverviewFilter.ALL_INVESTMENTS,
    energyConsumptionType: KPI.ConsumptionType.RAW_CONSUMPTION,
    carbonConsumptionType: KPI.ConsumptionType.RAW_CONSUMPTION,
    energyOrCarbonTrajectory: KPI.EnergyOrCarbonTrajectoryFilter.ENERGY,
    theoreticalOrAudit: KPI.TheoreticalOrAuditFilter.AUDIT,
  };

  private defaultAsyncFilters: { [filter: string]: Observable<number | string> } = {
    energyConsumptionType: this.configService.customerConfig$.pipe(map(config => config.default_consumption)),
    carbonConsumptionType: this.configService.customerConfig$.pipe(map(config => config.default_consumption)),
  }

  constructor(
    private configService: ConfigService,
  ) { }

  private _filter$: ObservableMap<number | string> = {
    budgetOrRatio: new BehaviorSubject<number>(null),
    budgetOrRatioCarbon: new BehaviorSubject<number>(null),
    investmentsStatus: new BehaviorSubject<number>(null),
    plannedInitiative: new BehaviorSubject<number>(null),
    forecastEmissionFactor: new BehaviorSubject<number>(null),
    initiativePriorities: new BehaviorSubject<number>(null),
    initiativePrioritiesByType: new BehaviorSubject<number>(null),
    initiativeSortingPriority: new BehaviorSubject<number>(null),
    feasibilityConsumptionOrEmission: new BehaviorSubject<number>(null),
    energyMeasurements: new BehaviorSubject<number>(null),
    consumptionAggregation: new BehaviorSubject<number>(null),
    energyConsumptionData: new BehaviorSubject<number>(null),
    energyConsumptionDisplay: new BehaviorSubject<number>(null),
    energyConsumptionBaselineDeviation: new BehaviorSubject<number>(null),
    emissionAggregation: new BehaviorSubject<number>(null),
    energyEmissionData: new BehaviorSubject<number>(null),
    energyEmissionDisplay: new BehaviorSubject<number>(null),
    energyEmissionBaselineDeviation: new BehaviorSubject<number>(null),
    decisionOverviewFilter: new BehaviorSubject<number>(null),
    energyConsumptionType: new BehaviorSubject<string>(null),
    carbonConsumptionType: new BehaviorSubject<string>(null),
    energyOrCarbonTrajectory: new BehaviorSubject<number>(null),
    theoreticalOrAudit: new BehaviorSubject<string>(null),
  }

  private cachedFilterLoaded: { [filter: string]: boolean } = {
    budgetOrRatio: false,
    budgetOrRatioCarbon: false,
    plannedInitiative: false,
    forecastEmissionFactor: false,
    initiativePriorities: false,
    initiativePrioritiesByType: false,
    initiativeSortingPriority: false,
    investmentsStatus: false,
    feasibilityConsumptionOrEmission: false,
    energyMeasurements: false,
    consumptionAggregation: false,
    energyConsumptionData: false,
    energyConsumptionDisplay: false,
    energyConsumptionBaselineDeviation: false,
    emissionAggregation: false,
    energyEmissionData: false,
    energyEmissionDisplay: false,
    energyEmissionBaselineDeviation: false,
    decisionOverviewFilter: false,
    energyConsumptionType: false,
    carbonConsumptionType: false,
    energyOrCarbonTrajectory: false,
    theoreticalOrAudit: false,
  };

  public filter$(type: FilterType, forceToNumber: boolean = true): Observable<number | string> {
    if (!this.cachedFilterLoaded[type]) {
      const cachedFilter = localStorage.getItem(this.storageKeys[type]);
      if (cachedFilter) {
        this._filter$[type].next(forceToNumber ? Number(cachedFilter) : cachedFilter);
      } else if (this.defaultAsyncFilters[type] !== undefined) {
        this.defaultAsyncFilters[type].pipe(first()).subscribe(value => {
          if (value) {
            this._filter$[type].next(value);
          } else {
            this.setDefaultFilter(type);
          }
        });
      } else {
        this.setDefaultFilter(type);
      }
      this.cachedFilterLoaded[type] = true;
    }
    return this._filter$[type].pipe(filter(value => value !== null));
  }

  public setDefaultFilter(type: FilterType): void {
    if (this.defaultFilters[type] !== undefined) {
      this._filter$[type].next(this.defaultFilters[type]);
    }
  }

  public setFilter(type: FilterType, value: number | string): void {
    if (value !== this._filter$[type].value) {
      this._filter$[type].next(value);
      localStorage.setItem(this.storageKeys[type], value?.toString() || null);
    }
  }
}
